import { FieldDefinitionType, Operator, Direction } from '@lp/lp-ui';
import type { FieldDefinition, Filter, Sort, Page, ManualRequestPagination, RequestPagination } from '@lp/lp-ui';
import type {
    ApiViewResponseError, ApiViewResponseResource, ApiViewResponse, ApiViewEntityResponse, ApiViewArrayResponse
} from '@lp/lp-ui/src/interfaces/searchFilter';
import identityService from './services/IdentityService';

// Re-export types from @lp/lp-ui to avoid local duplication
export { FieldDefinitionType, Operator, Direction };
export type {
    FieldDefinition, Filter, Sort, Page, RequestPagination,
    ApiViewResponseError, ApiViewResponseResource,
    ApiViewResponse, ApiViewEntityResponse, ApiViewArrayResponse
};

export interface OidcState {
    pathname: string
}

type GUID = string & { isGuid: true };

export function guid(guid: string): GUID {
    const pattern = new RegExp('^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$', 'i');
    return pattern.test(guid) === true
        ? guid as GUID
        : "00000000-0000-0000-0000-000000000000" as GUID;
}

export enum AuditLogType {
    None = 0,
    Create = 10,
    Update = 20,
    Delete = 30,
    EnableUser = 40,
    DisableUser = 50,
    UnlockUser = 60,
    UpdateUserName = 70,
    UpdateEmail = 80,
    ResetUserPassword = 90,
    ForceUserPasswordReset = 100,
    ChangeUserPassword = 110,
    ApplicationEnabled = 120,
    ApplicationDisabled = 130,
    SignIn = 140,
    SignOut = 150,
    Impersonate = 160,
    StopImpersonating = 170,
    EnableMfa = 180,
    DisableMfa = 190,
    RoleEnabled = 200,
    RoleDisabled = 210,
    WelcomeEmailSent = 220,
    UserSentToQlik = 230,
    InviteCreated = 240,
    InviteResent = 250,
    InviteAccepted = 260
}

export enum ContactType {
    Undefined = 0,
    Phone = 1,
    Mobile = 2,
    Fax = 3,
    Email = 4
}

export interface SelectInputOption {
    value: string | number,
    label: string
}

export const ContactTypeLabels: SelectInputOption[] = [
    { value: 0, label: "-" },
    { value: 1, label: "Phone" },
    { value: 2, label: "Mobile" },
    { value: 3, label: "Fax" },
    { value: 4, label: "Email" }
];

export enum AddressType {
    Default = 1
}

export interface PaginationRequest<T> {
    sort: Sort<T>,
    filters: Filter<T>[],
    page: Page,
    filterString: string
}

export interface PaginationResponse<T> {
    pageNumber: number,
    totalCount: number,
    items: T[]
}



export interface RoleModel {
    id: number,
    name: string,
    description: string
}

export interface BulkOperationResponse {
    operationId: string,
    totalUsers: number,
    label: string
}

export interface BulkOperationProgress {
    operationId: string,
    total: number,
    processed: number,
    label: string,
    succeeded: number,
    skipped: number,
    failed: number
}

export interface BulkRowFailureModel {
    rowNumber: number,
    email: string,
    reason: string
}

export interface UserContactDataModel {
    contact: string,
    contactType: ContactType,
    phoneExtension: string
}

export interface UserContactModel extends UserContactDataModel {
    id: number
}

export interface AddressDataModel {
    street1: string,
    street2: string,
    city: string,
    state: string,
    postalCode: string,
    countryAlpha3Code: string
}

export interface AddressModel extends AddressDataModel {
    id: number
}

export interface UserAddressModel {
    id: number,
    addressType: AddressType,
    address: AddressModel
}

export interface UserClientModel {
    clientId: number
}

export type InvitationStatus = 'Pending' | 'Accepted';

export interface UserInvitationSummaryModel {
    id: number,
    status: InvitationStatus,
    sentUtc?: Date | null,
    expiresUtc?: Date | null,
    lastDeliveryFailedUtc?: Date | null,
    resendCount: number
}

// No `enabled` on purpose: the server rejects a disabled invitee, so the state is not representable here either.
export interface InviteUserRequestModel {
    email: string,
    twoFactorEnabled: boolean,
    userClients: UserClientModel[],
    roleIds: number[],
    userOrganizations: OrganizationMemberModel[]
}

export interface InvitationResponseModel {
    invitationId: number,
    userId: number,
    email: string,
    // A bearer credential. Component state only — never dispatch it. Absent when the ticket step failed.
    url?: string | null,
    expiresUtc?: Date | null,
    warnings: string[]
}

export const InviteEmailCheckOutcome = {
    Available: 'available',
    ExpiredInvitee: 'expired-invitee',
    ExpiredInviteeForeign: 'expired-invitee-foreign',
    Taken: 'taken'
} as const;
export type InviteEmailCheckOutcome = typeof InviteEmailCheckOutcome[keyof typeof InviteEmailCheckOutcome];

export interface InviteEmailCheckModel {
    outcome: InviteEmailCheckOutcome,
    // Present only for expired-invitee, so the dialog can offer Resend for a user the caller may act on.
    userId?: number | null
}

export interface UserListItemModel {
    id: number,
    userName: string,
    firstName: string,
    lastName: string,
    fullName: string,
    email: string,
    phoneNumber: string,
    enabled: boolean,
    twoFactorEnabled: boolean,
    forcePasswordReset: boolean,
    welcomeEmailSent: boolean,
    dateCreatedUtc: Date,
    lastLoginDateUtc: Date | null,
    organizationsCsv: string,
    applicationId: number,
    roleId: number,
    // Filter carrier like applicationId/roleId; also the server's verdict "unaccepted invitation on an enabled user".
    invited: boolean,
    // Filter carrier only: the server never sends it. A row's own state is invitation.lastDeliveryFailedUtc.
    inviteNotDelivered?: boolean,
    // Absent on the wire when the user has no invitation row.
    invitation?: UserInvitationSummaryModel | null
}

export interface UserValidationDataModel {
    userName: string,
    email: string,
    phoneNumber: string,
    firstName: string,
    lastName: string
}

export interface UserPersonalDetailsModel {
    firstName: string,
    lastName: string,
    phoneNumber: string
}

export interface UserProfileDataModel extends UserPersonalDetailsModel {
    userName: string,
    email: string,
    enabled: boolean,
    twoFactorEnabled: boolean,
    forcePasswordReset: boolean,
    welcomeEmailSent: boolean
}

export interface UserDataModel extends UserProfileDataModel {
    contacts: UserContactModel[],
    userAddresses: UserAddressModel[],
    userClients: UserClientModel[],
    roleIds: number[],
    userOrganizations: OrganizationMemberModel[]
}

export interface UserModel extends UserDataModel {
    id: number,
    externalId: string,
    emailConfirmed: boolean,
    phoneNumberConfirmed: boolean,
    twoFactorEnabled: boolean,
    lockoutEnabled: boolean,
    accessFailedCount: number,
    welcomeEmailSent: boolean,
    dateCreatedUtc: Date,
    lastLoginDateUtc: Date | null
}

export interface CurrentUserModel extends UserModel {
    isTenantAdmin: boolean,
    inviteEnabled: boolean,
    organization: OrganizationModel,
    roles: string[],
    applications: ApplicationModel[],
    organizations: OrganizationModel[]
}

export interface UserProfileResponseModel extends UserModel {
    password: string
}

export interface UserPasswordModel {
    id: number,
    userName: string,
    firstName: string,
    lastName: string,
    password: string
}

export interface CountryModel {
    countryAlpha3Code: string,
    name: string
}

export interface ApplicationModel {
    id: number,
    clientId: string,
    clientName: string,
    description: string,
    clientUri: string,
    logoUri: string
}

export interface AuditLogUser {
    id: number,
    userName: string,
    firstName: string,
    lastName: string
}

export interface AuditLog {
    id: number,
    entityId: string,
    entityType: string,
    rootEntityId: string,
    rootEntityType: string,
    user: AuditLogUser,
    action: AuditLogType,
    actionLabel: string,
    timestamp: string
}

export interface AuditLogDetails extends AuditLog {
    newValues: { [key: string]: object },
    oldValues: { [key: string]: object }
}

export interface OrganizationModel {
    id: number,
    name: string,
    displayName: string,
    isProtected?: boolean
}

export interface OrganizationMemberModel {
    organizationId: number,
    organizationName: string,
    organizationDisplayName: string,
    roles: RoleModel[]
}

export interface BulkImportUserRowResult {
    rowNumber: number,
    success: boolean,
    userName: string,
    email: string,
    errors: string[]
}

export interface BulkImportUsersResultModel {
    operationId?: string,
    totalRows: number,
    successCount: number,
    failureCount: number,
    results: BulkImportUserRowResult[]
}

// "user" fieldName is not a leaf of AuditLog (it's a nested object),
// so we use FieldDefinition<any> for compatibility
export const AuditLogFields: FieldDefinition<any>[] = [
    {
        fieldName: "id",
        type: FieldDefinitionType.string,
        displayName: "ID",
        filterable: false,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "entityId",
        type: FieldDefinitionType.string,
        displayName: "Entity ID",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "entityType",
        type: FieldDefinitionType.string,
        displayName: "Entity Type",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "rootEntityId",
        type: FieldDefinitionType.string,
        displayName: "Root Entity ID",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "rootEntityType",
        type: FieldDefinitionType.string,
        displayName: "Root Entity Type",
        filterable: false,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "user",
        type: FieldDefinitionType.string,
        displayName: "User",
        filterable: false,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "actionLabel",
        type: FieldDefinitionType.string,
        displayName: "Action",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "timestamp",
        type: FieldDefinitionType.DateTime,
        displayName: "Timestamp",
        filterable: true,
        sortable: false,
        columnable: false
    }
]

const ApplicationDefaultPagination: RequestPagination<ApplicationModel> = {
    sort: { sortBy: 'clientName', sortDirection: Direction.Ascending },
    filters: [],
    page: { pageSize: 100, pageNumber: 1 },
    filterString: '',
    loaded: false
};

const RoleDefaultPagination: RequestPagination<RoleModel> = {
    sort: { sortBy: 'name', sortDirection: Direction.Ascending },
    filters: [],
    page: { pageSize: 100, pageNumber: 1 },
    filterString: '',
    loaded: false
};

export const UserFields: FieldDefinition<UserListItemModel>[] = [
    {
        fieldName: "id",
        type: FieldDefinitionType.number,
        displayName: "ID",
        filterable: false,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "userName",
        type: FieldDefinitionType.string,
        displayName: "User Name",
        filterable: true,
        sortable: true,
        columnable: true
    },
    {
        fieldName: "fullName",
        type: FieldDefinitionType.string,
        displayName: "Full Name",
        filterable: false,
        sortable: true,
        columnable: true
    },
    {
        fieldName: "firstName",
        type: FieldDefinitionType.string,
        displayName: "First Name",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "lastName",
        type: FieldDefinitionType.string,
        displayName: "Last Name",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "email",
        type: FieldDefinitionType.string,
        displayName: "Email",
        filterable: true,
        sortable: true,
        columnable: true
    },
    {
        fieldName: "phoneNumber",
        type: FieldDefinitionType.string,
        displayName: "Phone",
        filterable: true,
        sortable: true,
        columnable: true
    },
    {
        fieldName: "dateCreatedUtc",
        type: FieldDefinitionType.Date,
        displayName: "Date Created",
        filterable: true,
        sortable: true,
        columnable: true
    },
    {
        fieldName: "lastLoginDateUtc",
        type: FieldDefinitionType.Date,
        displayName: "Last Login Date",
        filterable: true,
        sortable: true,
        columnable: true
    },
    {
        fieldName: "enabled",
        type: FieldDefinitionType.boolean,
        displayName: "Enabled",
        filterable: true,
        sortable: true,
        columnable: true
    },
    {
        fieldName: "twoFactorEnabled",
        type: FieldDefinitionType.boolean,
        displayName: "Two Factor Enabled",
        filterable: true,
        sortable: true,
        columnable: false
    },
    {
        fieldName: "forcePasswordReset",
        type: FieldDefinitionType.boolean,
        displayName: "Force Password Reset",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "welcomeEmailSent",
        type: FieldDefinitionType.boolean,
        displayName: "Welcome Email Sent",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "invited",
        type: FieldDefinitionType.boolean,
        displayName: "Invited",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "inviteNotDelivered",
        type: FieldDefinitionType.boolean,
        displayName: "Invite Not Delivered",
        filterable: true,
        sortable: false,
        columnable: false
    },
    {
        fieldName: "organizationsCsv",
        type: FieldDefinitionType.string,
        displayName: "Organizations",
        filterable: false,
        sortable: false,
        columnable: true
    },
    {
        fieldName: "applicationId",
        type: FieldDefinitionType.customSearch,
        sortable: false,
        filterable: true,
        displayName: "Application",
        columnable: false,
        customSearchOptions: {
            label: 'Search Application',
            placeholder: 'Search by application name',
            optionLabelKey: ['clientName'],
            optionLabelKeyReadonly: ['clientName'],
            optionValueKey: 'id',
            defaultPagination: ApplicationDefaultPagination,
            searchUrlFunc: () => identityService.searchApplications(),
            fetchByIdFunc: (id: string | number) => identityService.getApplication(Number(id))
        }
    },
    {
        fieldName: "roleId",
        type: FieldDefinitionType.customSearch,
        sortable: false,
        filterable: true,
        displayName: "Role",
        columnable: false,
        customSearchOptions: {
            label: 'Search Role',
            placeholder: 'Search by role name',
            optionLabelKey: ['name'],
            optionLabelKeyReadonly: ['name'],
            optionValueKey: 'id',
            defaultPagination: RoleDefaultPagination,
            searchUrlFunc: () => identityService.searchRoles(),
            fetchByIdFunc: (id: string | number) => identityService.getRole(Number(id))
        }
    }
]

export interface UsersPagination extends ManualRequestPagination<UserListItemModel> {
    organizationId: number
}

export interface SetUserRolesRequest {
    userId: number,
    roleIds: number[]
}

export interface SetOrganizationUserRolesRequest extends SetUserRolesRequest {
    organizationId: number
}

export interface BulkUserActionRequest {
    isSelectAll: boolean
    selectedUserIds: number[]
    unSelectedUserIds: number[]
    filters: RequestPagination<UserListItemModel> | null
    organizationId: number
}

export interface BulkEnableDisableRequest extends BulkUserActionRequest {
    enable: boolean
}

export interface BulkMfaOnOffRequest extends BulkUserActionRequest {
    enableMfa: boolean
}

export interface BulkAssignRolesRequest extends BulkUserActionRequest {
    roleIds: number[]
    enable: boolean
}

export interface BulkEnableApplicationsRequest extends BulkUserActionRequest {
    clientIds: number[]
    enable: boolean
}

export interface BulkAddToOrganizationRequest extends BulkUserActionRequest {
    targetOrganizationId: number
    organizationRoleIds: number[]
}
